import React from "react";
import { CancelTokenSource } from "axios";
import { Button } from "@/components/ui/button";
import { getETA, getUploadInfo } from "../FileUpload/helpers";
import { XIcon, CircleCheckBig, Upload } from "lucide-react";

export interface UploadInfo {
  [name: string]: {
    is_uploaded: boolean;
    total: number;
    loaded: number;
    upload_id?: string;
    sources: CancelTokenSource[];
  };
}

interface UploadProgressBarProps {
  isUploadStarted: boolean;
  uploadInfo: UploadInfo;
  uploadStartTime?: Date;
  onCancel: () => void;
  menuOpen?: boolean;
}

const UploadProgressBar: React.FC<UploadProgressBarProps> = ({
  isUploadStarted,
  uploadInfo,
  uploadStartTime,
  onCancel,
}) => {
  if (!isUploadStarted) return null;

  const { loaded, total } = Object.values(uploadInfo).reduce(
    (result, v) => {
      const _loaded = v.loaded > v.total ? v.total : v.loaded;
      return {
        total: result.total + v.total,
        loaded: result.loaded + _loaded,
      };
    },
    { total: 0, loaded: 0 },
  );

  const uploadCompleted = Object.values(uploadInfo).every((v) => v.is_uploaded);

  return (
    <div
      id="progress-bar"
      className="absolute left-14 bottom-13 flex items-center gap-2 z-51"
    >
      <span className="bg-accent-foreground p-1.5 rounded-[8px]">
        <Upload className="h-4 w-4 text-primary-foreground" />
      </span>
      <div className="w-100 h-2 bg-accent-foreground rounded-[8px]">
        <div
          className="h-2 rounded-[8px] bg-[#34C2E5]"
          style={{ width: `${(loaded / total) * 100}%` }}
        />
      </div>
      {!uploadCompleted && (
        <Button
          onClick={onCancel}
          className="bg-accent-foreground !p-1.5 h-min"
          type="button"
        >
          <XIcon />
        </Button>
      )}
      {uploadCompleted && <CircleCheckBig />}
      <div className="text-sm">{getUploadInfo(loaded, total)}</div>
      {!!loaded && !uploadCompleted && uploadStartTime && (
        <div className="text-sm">
          (ETA: {getETA(uploadStartTime, loaded, total)})
        </div>
      )}
    </div>
  );
};

export default UploadProgressBar;
